import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { StatusBadge, StatusBadgeType } from '@/components/ui/status-badge';
import { ENROLLMENT_STATUS } from '@/types/enrollment-status';
import { Enrollments } from '@/types/enrollments';
import { getNextOpenCharge, isChargeOverdue } from '@/utils/charge-status';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { getChargeFrequencySummary } from '@/utils/get-charge-frequency-summary';
import { getServiceFrequencySummary } from '@/utils/get-service-frequency-sumary';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { View, type ViewStyle } from 'react-native';

type EnrollmentCardProps = {
  enrollment: Enrollments;
  size?: 'large' | 'small';
  basePath?: string;
  variant?: 'provider' | 'client';
};

const cardStyle: ViewStyle = {
  borderCurve: 'continuous',
  boxShadow: '0 1px 2px rgba(23, 17, 10, 0.04), 0 4px 12px rgba(23, 17, 10, 0.05)',
};

const getServiceScheduleSummaryText = (enrollment: Enrollments): string => {
  const schedules = enrollment.serviceSchedules;
  if (!schedules || schedules.length === 0) {
    const startDateFormatted = formatDate(enrollment.startDate);
    return `Data única (${startDateFormatted})`;
  }
  const firstScheduleSummary = getServiceFrequencySummary(schedules[0]);

  if (schedules.length > 1) {
    return `${firstScheduleSummary} (+ ${schedules.length - 1} regra${schedules.length > 2 ? 's' : ''})`;
  }
  return firstScheduleSummary;
};

const getPaymentStatusBadge = (enrollment: Enrollments): StatusBadgeType => {
  if (enrollment.status !== ENROLLMENT_STATUS.ACTIVE) {
    return {
      type: 'enrollment' as const,
      value: enrollment.status,
    };
  }

  const charges = enrollment.charges;
  if (!charges || charges.length === 0) {
    return {
      type: 'custom' as const,
      value: 'waiting',
      label: 'Aguardando 1ª cobrança',
      variant: 'info' as const,
    };
  }

  const pendingCharge = getNextOpenCharge(charges);

  if (pendingCharge && isChargeOverdue(pendingCharge)) {
    return {
      type: 'custom' as const,
      value: 'overdue',
      label: 'Atrasado',
      variant: 'error' as const,
    };
  }

  if (pendingCharge) {
    return {
      type: 'custom' as const,
      value: 'pending',
      // Cobrança pendente futura não é atraso; tratamos como "Pendente"
      label: 'Pendente',
      variant: 'warning' as const,
    };
  }

  return {
    type: 'custom' as const,
    value: 'paid',
    label: 'Pago',
    variant: 'success' as const,
  };
};

function InitialAvatar({ name, size = 36 }: { name: string; size?: number }) {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <View
      className="items-center justify-center rounded-full bg-primary-50"
      style={{ width: size, height: size }}
    >
      <ThemedText
        className="font-bold text-primary-600"
        style={{ fontSize: size * 0.42, lineHeight: size * 0.55 }}
      >
        {initial}
      </ThemedText>
    </View>
  );
}

function MetaRow({
  icon,
  text,
}: {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  text: string;
}) {
  return (
    <View className="flex-row items-center gap-1.5">
      <Ionicons className="text-neutral-400" name={icon} size={13} />
      <ThemedText className="flex-1 text-xs text-neutral-500" numberOfLines={1}>
        {text}
      </ThemedText>
    </View>
  );
}

export function EnrollmentCard({
  enrollment,
  size = 'large',
  basePath,
  variant = 'provider',
}: EnrollmentCardProps) {
  const paymentStatusBadge = getPaymentStatusBadge(enrollment);

  const handlePress = () => {
    if (!enrollment.id) {
      console.warn('Enrollment ID is missing, cannot navigate');
      return;
    }
    const path = (basePath || '/(tabs)/(provider)/enrollments') as any;
    const serviceIdParam = enrollment.service?.id ? `?serviceId=${enrollment.service.id}` : '';
    router.push(`${path}/${enrollment.id}${serviceIdParam}` as any);
  };

  const serviceSummary = getServiceScheduleSummaryText(enrollment);

  const title =
    variant === 'client'
      ? (enrollment.service?.name ?? 'Serviço sem nome')
      : (enrollment.client?.user?.username ?? 'Cliente não encontrado');

  if (size === 'small') {
    const serviceName = enrollment.service?.name ?? 'Serviço';
    const subtitle =
      variant === 'client'
        ? formatCurrency(enrollment.price)
        : (enrollment.client?.user?.username ?? 'Cliente...');

    return (
      <PressableScale
        haptic
        onPress={handlePress}
        accessibilityRole="button"
        className="mb-2 w-full flex-row items-center gap-3 rounded-xl border border-neutral-900/5 bg-card px-3 py-3"
        style={cardStyle}
      >
        <InitialAvatar name={variant === 'client' ? serviceName : subtitle} size={36} />
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-sm text-card-foreground" numberOfLines={1}>
            {serviceName}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
            {subtitle} · {formatDate(enrollment.startDate)}
          </ThemedText>
        </View>
        <View className="items-end gap-1">
          <StatusBadge status={paymentStatusBadge} size="xs" />
        </View>
        <Ionicons className="text-neutral-300" name="chevron-forward" size={16} />
      </PressableScale>
    );
  }

  return (
    <PressableScale
      haptic
      onPress={handlePress}
      accessibilityRole="button"
      className="mb-3 w-full gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={cardStyle}
    >
      <View className="flex-row items-center gap-3">
        <InitialAvatar name={title} size={40} />
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-base text-card-foreground" numberOfLines={1}>
            {title}
          </ThemedText>
          <ThemedText
            className="font-semibold text-sm text-primary"
            style={{ fontVariant: ['tabular-nums'] }}
          >
            {formatCurrency(enrollment.price)}
          </ThemedText>
        </View>
        <StatusBadge status={paymentStatusBadge} size="sm" />
      </View>

      <View className="gap-1">
        <MetaRow
          icon="calendar-outline"
          text={`${formatDate(enrollment.startDate)} – ${
            enrollment.endDate ? formatDate(enrollment.endDate) : 'Atual'
          }`}
        />
        <MetaRow
          icon={variant === 'client' ? 'repeat-outline' : 'construct-outline'}
          text={serviceSummary}
        />
        <MetaRow icon="card-outline" text={getChargeFrequencySummary(enrollment.chargeSchedule)} />
      </View>
    </PressableScale>
  );
}
